/**
 * What a heritage leg SAYS, in the album panel's words (TEM-1591, lineage W14).
 *
 * ONE FILE AT TWO PATHS, BYTE-IDENTICAL:
 *
 *   apps/dj/src/lib/heritageLegs.ts                 DJ heritage rail
 *   apps/radio/frontend/src/utils/heritageLegs.ts   Radio stream page
 *
 * Radio is Create React App and cannot import from outside its src/, so the
 * rule is held by a test instead of a module boundary:
 * `apps/dj/src/lib/heritageLegs.test.ts` fails if the copies differ by a byte,
 * if a tier word is not `LINEAGE_TIER_WORDS` in
 * `apps/record/web/src/lineage/tiers.shared.ts` (the vocabulary Rich approved,
 * W11), if a source name is not its `SOURCE_NAMES`, or if the count is not
 * W10's words. Edit the DJ copy, then run
 * `python3 scripts/audit/check_parity_copies.py --fix` (TEM-1727): the pair is
 * declared in `scripts/audit/parity_copies.json` and CI's `lint-repo-hygiene`
 * fails on any drift.
 *
 * Nothing here classifies an edge type. The DJ producer
 * (`apps/dj/src-tauri/src/upload/lineage.rs`) stamps each leg with its tier —
 * decided once, in Rust, by `tempest_ecosystem_types::lineage::heritage` — and
 * this file only words it. A leg without a known tier is not drawn: never as
 * its raw edge_type ("Embedding cluster member"), and never on a published set.
 *
 * A CREDITED leg also carries its role (TEM-1591 follow-up, Rich 2026-09-25):
 * "Composer", "Producer", "on bass", "on vocals" — the album panel's `roleOf`
 * words, stamped by DJ from `tempest_ecosystem_types::lineage::roles`, which a
 * Rust test holds to `tiers.shared.ts`. The chip prints it in place of
 * "Credited"; a credit that states no role still says "Credited". There is no
 * second role vocabulary here to drift.
 *
 * Imports nothing, and reads with `typeof` rather than truthiness: Radio reads
 * the legs out of a schemaless DynamoDB passthrough.
 */

/** The tiers a leg can carry — the panel's `LineageTier` tokens, in leg order. */
export type HeritageTier = 'credited' | 'sounds' | 'listeners';

export const HERITAGE_TIERS: readonly HeritageTier[] = ['credited', 'sounds', 'listeners'];

/** The panel's word for each tier, shown on the chip. TEM-1597: the `sounds`
 *  tier is a match between two records' style vectors — an embedding of their
 *  TEXT description, not their audio — so it says "Similar in style". */
export const HERITAGE_TIER_WORDS: Readonly<Record<HeritageTier, string>> = {
  credited: 'Credited',
  sounds: 'Similar in style',
  listeners: 'Listeners',
};

/** Who says so, in the panel's words, by extractor. */
export const HERITAGE_SOURCE_NAMES: Readonly<Record<string, string>> = {
  musicbrainz: 'MusicBrainz',
  listenbrainz: 'ListenBrainz',
  lastfm: 'Last.fm',
  embedding_edges: 'style matching',
  discogs_personnel: 'Discogs',
  discogs_credits: 'Discogs',
  discogs: 'Discogs',
  genius: 'Genius',
  wikidata: 'Wikidata',
  setlistfm: 'setlist.fm',
  riddim: 'the Riddim Archive',
  consensus: 'several sources',
  pressing_lineage: 'your pressings',
  bandcamp: 'Bandcamp',
};

/** The leg's tier, or null: a narrative-only leg, the no-lineage sentinel, a
 *  leg uploaded before legs carried a tier, or anything malformed. */
export function heritageTier(leg: unknown): HeritageTier | null {
  if (!leg || typeof leg !== 'object') return null;
  const tier: unknown = (leg as { tier?: unknown }).tier;
  if (typeof tier !== 'string') return null;
  return (HERITAGE_TIERS as readonly string[]).includes(tier) ? (tier as HeritageTier) : null;
}

/** A credited leg's role as DJ stamped it ("Composer", "on bass"), or null:
 *  a credit that states none, a leg that is not a credit, or anything
 *  malformed. */
export function heritageRole(leg: unknown): string | null {
  if (heritageTier(leg) !== 'credited') return null;
  const role: unknown = (leg as { role?: unknown }).role;
  if (typeof role !== 'string') return null;
  const word = role.trim();
  return word ? word : null;
}

/** The chip's label — a credit's role, else the tier's word — or null when
 *  the leg is not one to draw. */
export function heritageLabel(leg: unknown): string | null {
  const tier = heritageTier(leg);
  if (tier === null) return null;
  return heritageRole(leg) ?? HERITAGE_TIER_WORDS[tier];
}

/** May this leg be shown on a published set? Credits only (Rich, TEM-1574):
 *  a style match or a listener pair is never heritage there, and a leg with no
 *  tier cannot be vouched for. */
export function isPublishedHeritage(leg: unknown): boolean {
  return heritageTier(leg) === 'credited';
}

/** The extractor behind a leg, as a reader would say it. */
export function heritageSource(extractor: unknown): string {
  if (typeof extractor !== 'string' || !extractor.trim() || extractor === 'unknown') {
    return 'another source';
  }
  if (Object.prototype.hasOwnProperty.call(HERITAGE_SOURCE_NAMES, extractor)) {
    return HERITAGE_SOURCE_NAMES[extractor];
  }
  return extractor.split('_').filter(Boolean).join(' ');
}

/** W10's words for the names credited: "at least 12 names credited". The
 *  credited legs are distinct names credited on the record, capped, so the
 *  count is a lower bound. */
export function creditedNamesLabel(count: number): string {
  return `at least ${count.toLocaleString('en-US')} ${count === 1 ? 'name' : 'names'} credited`;
}
